import React, { useEffect } from 'react'
import withPhoto from '../../containers/withPhoto'
import MediaPicker from '../forms/MediaPicker'
import { IValueEditorProps } from '@app-types/general'
import { getPhotoIdFromKey, getPhotoKeyFromId } from '@utils/photo'
import { createLogger } from '@utils/logger'
import useValueEditor from '@hooks/useValueEditor'

// Initialize logger
const log = createLogger('PhotoEditor', { prependScope: true })
log.setLevel('info')

const WrappedMediaPicker = withPhoto(MediaPicker)

// Define interfaces for props and related data structures

type PhotoEditorProps = Omit<IValueEditorProps, 'value' | 'parseValue' | 'serializeValue' | 'onSave'> & {
  value?: string | number | null
  thumbnailStyle?: string
  parseValue?: (value: string | number | null) => number | null
  serializeValue?: (value: number | null) => string | number | null
  onSave?: (value: string | number | null) => void
}

type MediaPickerChangeObj = {
  value: number
}

// Default parse: external (string|null) → internal (number|null)
const defaultParseValueFn = (value: string | number | null): number | null => {
  const parsed = getPhotoIdFromKey(value)
  return parsed === null ? null : parsed
}

// Default serialize: internal (number|null) → external (string|null)
const defaultSerializeValueFn = (value: number | null): string | null => {
  if (value == null) return null
  return getPhotoKeyFromId(value)
}

// Guard to make sure we deal with numbers or nulls and not other types
const throwUnlessNumberOrNull = (val: any) => {
  if (val !== null && typeof val !== 'number') {
    throw new Error(`PhotoEditor: value must be a number or null, got ${val} (a ${typeof val})`)
  }
}

const PhotoEditor: React.FC<PhotoEditorProps> & { EDITOR_TYPE: string } = ({
  name,
  value = null,
  onChange,
  onSave,
  onEditCancel,
  thumbnailStyle = 'small_square',
  parseValue = defaultParseValueFn,
  serializeValue = defaultSerializeValueFn,
  ...restProps
}) => {
  // Guard function to make sure we deal with ids as numbers internally
  const wrappedParseValue = (val: string | number | null): number | null => {
    const result = parseValue(val)
    throwUnlessNumberOrNull(result)
    return result
  }

  // Hook usage:
  // The `useValueEditor` hook is used to manage the transformation between the external `value`
  // (which may be a string, number, or null) and the internal `localValue` (which is always a number or null).
  //
  // - `parseValue`: Converts the external value into a number or null (e.g., from "photo_123" → 123).
  // - `serializeValue`: Converts the internal value back into the expected external format.
  // - `onChange`: Fires when `localValue` changes, ensuring `onChange` receives the correctly serialized value.
  // - `onSave`: Triggers when the user commits the change, providing the serialized external value.
  // - `onEditCancel`: Called if editing is canceled.
  //
  // This allows the component to handle both string-based and numeric IDs while enforcing internal consistency.
  type ExternalValue = string | number | null
  type InternalValue = number | null
  const { localValue, handleLocalChange, handleBlurOrSave } = useValueEditor<ExternalValue, InternalValue>({
    value: value,
    parseValue: wrappedParseValue,
    serializeValue,
    onChange: onChange
      ? ({ value }) => {
          // value is string|null
          // onChange expects string|number|null, and string|null is assignable to that
          onChange({ name, value })
        }
      : undefined,
    onSave: onSave
      ? value => {
          // value is number|string|null
          // onSave expects string|number|null, string|null is valid
          onSave(value)
        }
      : undefined,
    onEditCancel,
  })

  // Update local value when externalValue or parseValue changes
  useEffect(() => {
    const parsedValue = wrappedParseValue(value)
    updateLocalValue(parsedValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, parseValue])

  const updateLocalValue = (newValue: number | null) => {
    handleLocalChange(newValue)
  }

  const handlePhotoSelect = (changeObj: MediaPickerChangeObj) => {
    // If changeObj.value is a string, convert it to a number
    const numericValue = typeof changeObj.value === 'string' ? Number(changeObj.value) : changeObj.value
    updateLocalValue(numericValue)
  }

  const handleSave = () => {
    handleBlurOrSave()
  }

  return (
    <span className="content-editor content-editor--photo photo-editor">
      <WrappedMediaPicker
        key="media-picker"
        name={name}
        value={localValue} // number|null internally
        onChange={handlePhotoSelect}
        thumbnailStyle={thumbnailStyle}
        apiVersion={2}
        {...restProps}
      />
    </span>
  )
}

PhotoEditor.EDITOR_TYPE = 'photo'

export default PhotoEditor
